import ExpandedRowRenderer from './EditableDataGrid/ExpandedRowRenderer';
import React, { ReactElement, useContext, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useComponentQueryErrorHandling } from '../utils/parseErrorResponse';
import getIn from './connectedForm/utils/getIn';
import { useLazyQuery } from '@apollo/client';
import {
  IAssemblyComponent,
  IProduct,
  IProductComponent,
  IProductComponentInformationQueryResult,
  IProductComponentInformationQueryVariables,
} from '../graphql/schema/p4types';
import ProductComponentInformationQuery from '../graphql/queries/product/ProductComponentInformationQuery.graphql';
import { IEditableDataGridColumn } from '../@types/editableDataGridTypes';
import DropdownEditorClean from './DropdownEditor/DropdownEditorClean';
import { Product } from '../graphql/entities';
import EditableDataGrid from './EditableDataGrid';
import styled from 'styled-components';
import Formatters from './grid-addons/formatters';
import ExpansionContext from './Context/ExpansionContext';

const PaddedGridContainer = styled.div`
  padding: 0.5em 1em;
`;

type Component = { id: number; component: IProduct } & any;
type Parent = {
  components: Array<Component>;
  product: IProduct;
  quantity: number;
} & any;

type ComponentGridPropType = {
  path?: string;
  parent: Parent;
  onProductSelected?: (productId: number, path: string | undefined, dataId: string) => void;
  onChange: (path: string, value: any) => void;
  extraColumns?: Array<IEditableDataGridColumn>;
  readonly?: boolean;
  deleteEnabled?: boolean;
  onRowClick?: (rowIndex: number, component: Component, path?: string) => void;
  screenName?: string;
  renderActionButtons?: ({ rowData: any, rowIdx: number }) => Array<JSX.Element | ReactElement>;
  extraRowEnabled?: boolean;
};

const ComponentExpansion = (props: { idx?: number; rowSelectionPrefix: string } & ComponentGridPropType & any) => {
  return (
    <ExpandedRowRenderer
      {...props}
      expansion={(expansionProps: any) => {
        const index = expansionProps.idx! + 1;
        const newPath = expansionProps.path ? `${expansionProps.path}.${index}` : `${index}`;
        return (
          <PaddedGridContainer>
            {newPath.indexOf('.') !== -1 ? <p>{newPath}</p> : null}
            <ComponentGrid {...expansionProps} path={newPath} />
          </PaddedGridContainer>
        );
      }}
    />
  );
};

/**
 * Displays components of a Kit/Assembly in nested grids and allows editing
 *
 * @param path current path in the component hierarchy
 * @param parent the parent entity holding the initial set of components e.g. product/order line/assembly
 * @param onProductSelected callback for when a component in the hierarchy is selected
 * @param onChange callback for when a field on a component is changed
 * @param onRowClick callback for when a row in the grid is clicked
 * @param extraColumns extra columns to include in the grid
 * @param readonly whether the grid(s) are read only
 * @param screenName to form part of the data-testid on EditableDataGrid, for E2E
 * @constructor
 */
export const ComponentGrid = ({
  path,
  parent,
  onProductSelected,
  onChange,
  onRowClick,
  extraColumns,
  readonly = false,
  deleteEnabled = true,
  screenName,
  renderActionButtons,
  extraRowEnabled = false,
}: ComponentGridPropType) => {
  const dispatch = useDispatch();
  const handleQueryError = useComponentQueryErrorHandling();
  const { setOpenMap } = useContext(ExpansionContext);

  /**
   * Calculates the data ID (dot delimited path) to the components in the Redux store for the component hierarchy path
   * (e.g. 1.1.2.1) provided to this React component
   */
  const dataId = useMemo(() => {
    let field: string = 'components';
    if (path) {
      const pathSplit = path.split('.');
      field = pathSplit.reduce((acc, currentValue) => {
        return `${acc ? `${acc}.` : ''}${parseInt(currentValue, 10) - 1}.component.components`;
      }, field);
    }
    return field;
  }, [path]);
  const productId = !path ? parent?.product?.id : getIn(parent, `${dataId.substring(0, dataId.lastIndexOf('.'))}.id`);
  const warehouseId = parent?.warehouse?.id;
  const quantity = parent?.quantity ?? 0;

  const [queryProductComponentInformation] = useLazyQuery<
    IProductComponentInformationQueryResult,
    IProductComponentInformationQueryVariables
  >(ProductComponentInformationQuery, {
    onCompleted: (data) => {
      const info =
        data?.data?.map((asc) => {
          return {
            id: asc?.id,
            productComponent: {
              id: asc?.productComponentId,
              component: asc?.component,
              quantity: asc?.quantity,
              stockLevel: asc?.stockLevel,
              totalRequired: asc?.requiredQuantity,
              freeStock: asc?.freeStock,
              reserved: asc?.reserved,
              topLevel: asc?.topLevel,
              assigned: asc?.assigned,
              stockTotal: asc?.stockTotal,
              defaultBinLocation: asc?.defaultBinLocation,
            },
          };
        }) ?? [];
      onChange(dataId, info);
    },
    onError: handleQueryError,
  });

  useEffect(() => {
    if (productId === null || productId === undefined) {
      return;
    }

    if (onProductSelected) {
      onProductSelected(productId, path, dataId);
    } else if (warehouseId && productId && quantity) {
      queryProductComponentInformation({
        variables: {
          productId,
          warehouseId,
          assemblyQty: quantity,
        },
      }).then();
    }
  }, [productId, warehouseId, quantity, parent?.concurrencyVersion]);

  const COMPONENT_COLUMN_META = useMemo((): Array<IEditableDataGridColumn> => {
    let columns: Array<IEditableDataGridColumn> = [
      {
        name: 'Product',
        key: 'productComponent.component',
        editable: !path && !readonly,
        editor: <DropdownEditorClean />,
        options: Product,
        formatter: 'dropdown',
        width: 250,
        resizable: false,
      },
      {
        name: 'Description',
        key: 'productComponent.component.description',
        editable: false,
        resizable: true,
      },
      {
        name: 'Product Class',
        key: 'productComponent.component.productClass',
        editable: false,
        resizable: true,
      },
      {
        name: 'Levelled',
        key: 'productComponent.component.leveled',
        formatter: Formatters.BooleanFormatter,
        editable: false,
        resizable: true,
      },
      {
        name: 'Quantity',
        key: 'productComponent.quantity',
        formatter: 'numeric',
        editable: !path && !readonly,
        resizable: true,
        options: {
          format: {
            decimals: (rowData: IAssemblyComponent) => {
              return rowData?.productComponent?.component?.precision ?? 0;
            },
          },
        },
      },
    ];

    if (extraColumns) {
      columns = columns.concat(extraColumns);
    }

    return columns;
  }, [path, extraColumns, readonly]);

  const rowSelectionPrefix = `componentGrid${path ? `-${path}` : ''}`;

  const componentData = getIn(parent, dataId);
  if (!componentData || componentData.length === 0) {
    return <p>No components to display</p>;
  } else if (!warehouseId) {
    return <p>Select a warehouse to load component information</p>;
  }

  return (
    <EditableDataGrid
      contextKey={`assemblyEditableGrid-${path}`}
      columns={COMPONENT_COLUMN_META}
      expandableRows={true}
      listData={componentData}
      rowsCount={(componentData.length ?? 0) + (!path && !readonly && extraRowEnabled ? 1 : 0)}
      addBlankRow={!path && !readonly && extraRowEnabled}
      enableCellSelect={true}
      enableCellAutoFocus={false}
      renderActionButtons={renderActionButtons}
      enableContextMenu={true}
      rowRenderer={
        <ComponentExpansion
          rowSelectionPrefix={rowSelectionPrefix}
          path={path}
          parent={parent}
          onChange={onChange}
          onProductSelected={onProductSelected}
          onRowClick={onRowClick}
          extraColumns={extraColumns}
        />
      }
      onRowClick={(rowIdx: number, rowData: IProductComponent) => {
        if (componentData[rowIdx]?.component) {
          setOpenMap((currentOpenMap) => ({
            ...currentOpenMap,
            [rowData.uuid]: !currentOpenMap[rowData.uuid],
          }));
        }
        onRowClick?.(rowIdx, componentData[rowIdx], path);
      }}
      onRowUpdated={({ updated, cellKey, rowIdx }) => {
        if (!updated) return;

        onChange(`${dataId}.${rowIdx}.${cellKey as string}`, updated[cellKey as string]);
      }}
      deleteOption={
        deleteEnabled && !path
          ? (rowIdx) => {
              onChange(`${dataId}.${rowIdx}.deleted`, true);
            }
          : undefined
      }
      screenName={screenName}
    />
  );
};
